import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import cs from "classnames";

import {
  Box,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import {
  ArrowRightIcon,
  CloseRoundedIcon,
  KeyboardArrowDownIcon,
  MenuTwoBarsIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useIntersectionObserver } from "@js/hooks/use-intersection-observer";

import { RouterLink } from "../link";
import { Logo } from "../logo";

import type { NavLink, NavSubLink } from "./constants";
import { NAV_LINKS } from "./constants";
import { BOOK_DEMO_URL, getNavLinkToUrl, getSignUpLinkUrl } from "./helpers";
import { PublicTopHeader } from "./public-top-header";

import styles from "./public-top-navigation-mobile.module.scss";

export const PublicTopNavigationMobile = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const anchorRef = useRef(null);

  const isNearTopEdge = useIntersectionObserver(
    anchorRef,
    { threshold: 0.75 },
    true,
  );

  const handleMenuClick = () => {
    setIsNavOpen((prev) => !prev);
  };

  return (
    <>
      <Box ref={anchorRef} className={styles.pageOffset}></Box>
      <PublicTopHeader
        className={cs(styles.headerMobile, {
          [styles.headerShort]: !isNearTopEdge,
        })}
      >
        <Stack direction="row" className={cs(styles.navButtonContainer)}>
          <Logo size={120} />
          <IconButton
            aria-label="Open navigation"
            onClick={handleMenuClick}
            variant="transparent"
            disableRipple
            className={styles.menuButton}
            size="medium"
          >
            {isNavOpen ? (
              <CloseRoundedIcon sx={{ transform: "scale(1.1)" }} />
            ) : (
              <MenuTwoBarsIcon />
            )}
          </IconButton>
        </Stack>
        {isNavOpen && <NavLinksMenu />}
      </PublicTopHeader>
    </>
  );
};

const NavLinksMenu = () => {
  const isTabletOrLarge = !useMediaQuery("sm");
  const { search } = useLocation();

  return (
    <Box
      className={cs(styles.navContainer, {
        [styles.navContainerMedium]: isTabletOrLarge,
      })}
    >
      <Box component="nav" aria-label="Main menu">
        <Stack component="ul" className={styles.navLinks}>
          {NAV_LINKS.map(({ subLinks, subLinkGroups, ...linkData }) => {
            if (subLinks?.length) {
              return (
                <NavLinkExpandable
                  key={linkData.label}
                  subLinks={subLinks}
                  {...linkData}
                />
              );
            }

            if (subLinkGroups?.length) {
              return (
                <NavLinkWithGroupsExpandable
                  key={linkData.label}
                  subLinkGroups={subLinkGroups}
                  {...linkData}
                />
              );
            }

            return <BasicNavLink key={linkData.label} {...linkData} />;
          })}
        </Stack>
        <Stack sx={{ pl: "6px", alignItems: "start" }}>
          <Box
            className={cs(styles.signUpWrapper, {
              [styles.signUpWrapperMedium]: isTabletOrLarge,
            })}
          >
            <RouterLink className={styles.ctaLink} to={BOOK_DEMO_URL}>
              <span>Book Demo</span>
              <ArrowRightIcon />
            </RouterLink>
          </Box>

          <Stack direction="row" sx={{ alignItems: "center", gap: "18px" }}>
            <RouterLink
              className={styles.ctaLink}
              to={getSignUpLinkUrl(search)}
            >
              Talent Sign Up
            </RouterLink>
            <ArrowRightIcon />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

type NavSubLinkComponentProps = {
  subLink: NavSubLink;
  search: string;
  small?: boolean;
};

const NavSubLinkComponent = ({
  subLink,
  search,
  small,
}: NavSubLinkComponentProps) => {
  const { url, persistUTMSearchParams, label } = subLink;
  if (!url) {
    return (
      <Typography
        component="label"
        role="button"
        className={cs(styles.navLink, styles.navSubLink, {
          [styles.subLinkSmall]: small,
        })}
      >
        {label}
      </Typography>
    );
  }

  return (
    <Typography
      component="link"
      to={getNavLinkToUrl({ url, search, persistUTMSearchParams })}
      RouterLink={RouterLink}
      className={cs(styles.navLink, styles.navSubLink, {
        [styles.subLinkSmall]: small,
      })}
    >
      {label}
    </Typography>
  );
};

type NavLinkWithUnderlineProps = Pick<NavLink, "url" | "label">;

const NavLinkWithUnderline = ({ label, url }: NavLinkWithUnderlineProps) => {
  if (!url) {
    return (
      <Typography component="label" className={styles.navLink} role="button">
        {label}
      </Typography>
    );
  }

  return (
    <Typography
      component="link"
      to={url}
      RouterLink={RouterLink}
      className={styles.navLink}
    >
      {label}
    </Typography>
  );
};

type NavSubLinksProps = {
  subLinks: NonNullable<NavLink["subLinks"]>;
};

const NavSubLinks = ({ subLinks }: NavSubLinksProps) => {
  const { search } = useLocation();

  return (
    <Stack component="ul" className={styles.navSubLinks}>
      {subLinks.map((subLink) => {
        return (
          <Box key={subLink.label} component="li">
            <NavSubLinkComponent subLink={subLink} search={search} />
          </Box>
        );
      })}
    </Stack>
  );
};

type BasicNavLinkProps = Pick<
  NavLink,
  "url" | "label" | "persistUTMSearchParams"
>;

const BasicNavLink = ({
  url,
  label,
  persistUTMSearchParams,
}: BasicNavLinkProps) => {
  const { search } = useLocation();
  const toUrl = getNavLinkToUrl({ url, search, persistUTMSearchParams });

  return (
    <Stack
      direction="row"
      sx={{ height: "100%", alignItems: "center" }}
      className={styles.navLinkContainer}
    >
      <NavLinkWithUnderline url={toUrl} label={label} />
    </Stack>
  );
};

type NavLinkExpandableProps = BasicNavLinkProps &
  Pick<NavSubLinksProps, "subLinks">;

const NavLinkExpandable = ({
  url,
  label,
  subLinks,
}: NavLinkExpandableProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleClick = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <Stack
        direction="row"
        role="button"
        sx={{ height: "100%", alignItems: "center" }}
        className={styles.navLinkContainer}
        onClick={handleClick}
        aria-label={`Expand ${label}`}
        aria-expanded={isExpanded ? "true" : undefined}
      >
        <NavLinkWithUnderline url={url} label={label} />
        <KeyboardArrowDownIcon
          className={cs(styles.arrowIcon, { [styles.arrowIconUp]: isExpanded })}
        />
      </Stack>
      {isExpanded && <NavSubLinks subLinks={subLinks} />}
    </>
  );
};

type NavLinkGroupExpandableProps = BasicNavLinkProps &
  Pick<NavSubLinksProps, "subLinks">;

const NavLinkGroupExpandable = ({
  url,
  label,
  subLinks,
}: NavLinkGroupExpandableProps) => {
  const { search } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const handleClick = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <Stack
        direction="row"
        role="button"
        sx={{ height: "100%", alignItems: "center" }}
        className={styles.navLinkContainer}
        onClick={handleClick}
        aria-label={`Expand ${label}`}
        aria-expanded={isExpanded ? "true" : undefined}
      >
        <NavSubLinkComponent subLink={{ url, label }} search={search} small />
        <KeyboardArrowDownIcon
          className={cs(styles.arrowIcon, { [styles.arrowIconUp]: isExpanded })}
        />
      </Stack>
      {isExpanded && <NavSubLinks subLinks={subLinks} />}
    </>
  );
};

type NavSubLinkGroupsProps = {
  subLinkGroups: NonNullable<NavLink["subLinkGroups"]>;
};

const NavSubLinkGroups = ({ subLinkGroups }: NavSubLinkGroupsProps) => {
  return (
    <Stack>
      {subLinkGroups.map(({ url, label, subLinks }) => {
        return (
          <NavLinkGroupExpandable
            key={label}
            url={url}
            label={label}
            subLinks={subLinks}
          />
        );
      })}
    </Stack>
  );
};

type NavLinkWithGroupsExpandableProps = BasicNavLinkProps &
  NavSubLinkGroupsProps;

const NavLinkWithGroupsExpandable = ({
  url,
  label,
  subLinkGroups,
}: NavLinkWithGroupsExpandableProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleClick = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <Stack
        direction="row"
        role="button"
        sx={{ height: "100%", alignItems: "center" }}
        className={styles.navLinkContainer}
        onClick={handleClick}
        aria-label={`Expand ${label}`}
        aria-expanded={isExpanded ? "true" : undefined}
      >
        <NavLinkWithUnderline url={url} label={label} />
        <KeyboardArrowDownIcon
          className={cs(styles.arrowIcon, { [styles.arrowIconUp]: isExpanded })}
        />
      </Stack>
      {isExpanded && <NavSubLinkGroups subLinkGroups={subLinkGroups} />}
    </>
  );
};
